/**
 * ScoreDriverWaterfall — horizontal waterfall over
 * report.scoreDrivers showing each category's contribution to the
 * overall score.
 *
 * Each bar's `impact` value is signed: positive impact = category
 * pulls the overall score up, negative = pulls it down. A
 * ReferenceLine at x=0 separates positives (right of the line) from
 * negatives (left). Colour is theme-aware: positives use the green
 * end of the chart palette, negatives use the danger colour.
 *
 * Returns null when scoreDrivers is empty so a report without
 * sensitivity data doesn't render an empty section.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from 'recharts';
import { SafeResponsiveContainer } from '../../../ui/SafeResponsiveContainer';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { ReportTheme } from '../../../../types/reportTheme';
import { resolveChartGridChrome } from '../../../../lib/reportChartGrid';
import { CHART_PLOT_MARGIN, CHART_INK, CHART_FRAME_BOUNDS } from '../../../../lib/reportChartLayoutTokens';

interface Props {
  report: SiteRagReport;
  theme: ReportTheme;
}

export const ScoreDriverWaterfall: React.FC<Props> = ({ report, theme }) => {
  const drivers = report.scoreDrivers ?? [];
  if (drivers.length === 0) return null;

  const positiveColor = theme.positiveColor || '#1D9E75';
  const negativeColor = theme.negativeColor || '#A32D2D';
  // Grid + reference-line ink derived from the theme so the chrome reads
  // against the page background. Byte-stable (#e5e7eb / #666) on a white page.
  const { grid: gridColor, axis: axisColor } = resolveChartGridChrome(theme);
  // Sort by absolute impact descending so the headline drivers
  // sit at the top of the chart.
  const sorted = [...drivers].sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));

  // SHRINK-TO-FIT (RC-2) — this waterfall owns a bespoke ROW-COUNT-driven height
  // (30px/row) outside the ChartFrame contract; the height legitimately grows
  // with the driver count and the ChartFrame max-clamp would CLIP a tall
  // many-row chart, so we keep the row-driven height but bring it under the
  // SAME legibility FLOOR as the `bars` profile (240px, raised from the old
  // bespoke 180px): a 1-2 driver chart is no longer sub-legible, while ≥8
  // drivers still grow the canvas. No `transform: scale()`; pure height.
  const canvasHeight = Math.max(CHART_FRAME_BOUNDS.bars.legibilityFloor, sorted.length * 30);

  return (
    <div
      id="scoreDrivers"
      data-section="scoreDrivers"
      className="space-y-2"
      data-testid="score-driver-waterfall"
    >
      <h3 className="text-xs font-bold uppercase tracking-widest text-text-secondary">
        Score Drivers: Impact on Overall Score
      </h3>
      <div className="w-full" style={{ height: canvasHeight }} data-testid="score-driver-canvas" data-export-capture="" data-export-id="score-driver-canvas" data-export-title="Score Drivers">
        <SafeResponsiveContainer traceName="report:score-drivers" width="100%" height="100%" minWidth={100}>
          <BarChart layout="vertical" data={sorted} margin={CHART_PLOT_MARGIN.waterfall} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="2 4" stroke={gridColor} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: CHART_INK.axisTickFontSize }}
              tickLine={false}
              label={{ value: 'Impact (points)', position: 'insideBottom', offset: -5, fontSize: CHART_INK.axisLabelFontSize }}
            />
            <YAxis
              type="category"
              dataKey="category"
              tick={{ fontSize: CHART_INK.axisTickFontSize }}
              tickLine={false}
              width={140}
            />
            <ReferenceLine x={0} stroke={axisColor} />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={({ payload }) => {
                const d = payload?.[0]?.payload as (typeof drivers)[number] | undefined;
                if (!d) return null;
                return (
                  <div className="bg-card border border-border px-2 py-1 text-xs shadow-sm">
                    <div className="font-bold text-text-primary">{d.category}</div>
                    <div className="text-text-muted">Impact: {d.impact > 0 ? '+' : ''}{d.impact} pts</div>
                    <div className="text-text-muted">If improved → {d.ifImproved} · if worsened → {d.ifWorsened}</div>
                  </div>
                );
              }}
            />
            <Bar isAnimationActive={false} dataKey="impact" radius={[0, 2, 2, 0]}>
              {sorted.map((d, i) => (
                <Cell key={`cell-${i}`} fill={d.impact >= 0 ? positiveColor : negativeColor} />
              ))}
            </Bar>
          </BarChart>
        </SafeResponsiveContainer>
      </div>
    </div>
  );
};
